import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
  },
  pagination: {
    total: 0,
    total_pages: 0,
    current_page: 1,
    per_page: 25,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns: _state => campaignType => {
    return _state.records.filter(
      record => record.campaign_type === campaignType
    );
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
};

// Monotonic token for `get`. Typing/tab-switching fires overlapping requests;
// without sequencing a slower earlier response can resolve last and overwrite a
// newer list + pagination meta with stale data. Only the latest request commits.
let latestGetRequestId = 0;

export const actions = {
  get: async function getCampaigns({ commit }, params = {}) {
    latestGetRequestId += 1;
    const requestId = latestGetRequestId;
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsAPI.get(params);
      // A newer request started while this one was in flight — drop this result.
      if (requestId !== latestGetRequestId) return;
      // Support both legacy array response and new { data, meta } shape
      if (response.data && response.data.data && response.data.meta) {
        commit(types.SET_CAMPAIGNS, response.data.data);
        commit(types.SET_CAMPAIGN_PAGINATION, response.data.meta);
      } else if (Array.isArray(response.data)) {
        commit(types.SET_CAMPAIGNS, response.data);
      } else {
        // Fallback: try to set records from data
        commit(types.SET_CAMPAIGNS, response.data || []);
      }
    } catch (error) {
      // Ignore error
    } finally {
      // Only the latest request owns the loading flag, so a stale response
      // cannot flip isFetching off while the current fetch is still running.
      if (requestId === latestGetRequestId) {
        commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
      }
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  previewFilter: async (_, { payload, channel }) => {
    const response = await CampaignsAPI.previewFilter(payload, channel);
    return response.data;
  },
  previewLabels: async (_, { labelIds, channel }) => {
    const response = await CampaignsAPI.previewLabels(labelIds, channel);
    return response.data;
  },
  previewCsv: async (_, { blobSignedId, channel }) => {
    const response = await CampaignsAPI.previewCsv(blobSignedId, channel);
    return response.data;
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CAMPAIGN_PAGINATION](_state, meta) {
    _state.pagination = {
      ..._state.pagination,
      total: meta.total || meta.total_count || 0,
      total_pages: meta.total_pages || 0,
      current_page: meta.current_page || 1,
      per_page: meta.per_page || 25,
    };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
